import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import tagService from '../services/tagService';

/**
 * VRFFloorControl - Kat Bazında VRF Toplu Kontrol Widget'ı
 *
 * Tüm VRF cihazlarını tek seferde kontrol etmek için:
 * - Tümünü AÇ/KAPAT
 * - Toplu Set Sıcaklık
 * - Toplu Çalışma Modu
 * - Toplu Fan Modu
 */
const VRFFloorControl = ({
  id,
  text = 'Kat Kontrol',
  width = 140,
  height = 50,

  // Kat bilgisi
  floorNumber = 1,
  floorName = '1. Kat',

  // Bu kattaki tüm odaların tag listesi (JSON string veya array)
  roomTags = [], // [{roomName, onOffSetupTagId, setTempTagId, opModeSetupTagId, fanSetupTagId, setTempCoolTagId, setTempHeatTagId}, ...]

  // Company ID
  companyId = 4,

  // Görünüm
  backgroundColor = '#edbc45',
  foreColor = '#000000',
  borderColor = '#433316',
  borderWidth = 2,
  borderRadius = 8,
  fontSize = 14,

  // elementDetails (SCADA_Pages uyumu)
  elementDetails = {},
  properties = {},

  isEditMode = false,
  tagValues = {},

  ...props
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Toplu değerler
  const [bulkSetTemp, setBulkSetTemp] = useState('22');
  const [bulkOpMode, setBulkOpMode] = useState('1'); // 1=Soğutma, 2=Isıtma
  const [bulkFanMode, setBulkFanMode] = useState('5'); // 5=Auto, 1=Low, 3=Mid, 2=High

  // Props'tan veya elementDetails'dan room tag'lerini al
  const getRoomTags = () => {
    const tags = elementDetails?.roomTags || properties?.roomTags || roomTags;
    if (typeof tags === 'string') {
      try {
        return JSON.parse(tags);
      } catch {
        return [];
      }
    }
    return Array.isArray(tags) ? tags : [];
  };

  const rooms = getRoomTags();
  const effectiveFloorName = elementDetails?.floorName || properties?.floorName || floorName;
  const effectiveCompanyId = elementDetails?.companyId || properties?.companyId || companyId;

  // Çalışma modu seçenekleri
  const opModeOptions = [
    { value: '0', label: 'KAPALI' },
    { value: '1', label: 'SOĞUTMA' },
    { value: '2', label: 'ISITMA' },
    { value: '3', label: 'FAN' },
    { value: '4', label: 'AUTO' }
  ];

  // Fan modu seçenekleri
  const fanModeOptions = [
    { value: '5', label: 'AUTO' },
    { value: '1', label: 'DÜŞÜK' },
    { value: '4', label: 'ORTA' },
    { value: '2', label: 'YÜKSEK' }
  ];

  const handleOpenDialog = () => {
    if (!isEditMode) {
      setDialogOpen(true);
      setMessage(null);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setMessage(null);
  };

  // Tümünü AÇ
  const handleTurnAllOn = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let successCount = 0;
      for (const room of rooms) {
        if (room.onOffSetupTagId) {
          try {
            await tagService.updateTagValue(room.onOffSetupTagId, '1', effectiveCompanyId);
            successCount++;
          } catch (err) {
            console.error(`Error turning on room ${room.roomName}:`, err);
          }
        }
      }
      setMessage({ type: 'success', text: `${successCount} oda açıldı` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Tümünü KAPAT
  const handleTurnAllOff = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let successCount = 0;
      for (const room of rooms) {
        if (room.onOffSetupTagId) {
          try {
            await tagService.updateTagValue(room.onOffSetupTagId, '0', effectiveCompanyId);
            successCount++;
          } catch (err) {
            console.error(`Error turning off room ${room.roomName}:`, err);
          }
        }
      }
      setMessage({ type: 'success', text: `${successCount} oda kapatıldı` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Toplu Set Sıcaklık
  const handleBulkSetTemp = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let successCount = 0;
      for (const room of rooms) {
        // Çalışma moduna göre doğru tag'i seç
        const tagId = bulkOpMode === '1'
          ? (room.setTempCoolTagId || room.setTempTagId)
          : bulkOpMode === '2'
            ? (room.setTempHeatTagId || room.setTempTagId)
            : room.setTempTagId;

        if (tagId) {
          try {
            await tagService.updateTagValue(tagId, bulkSetTemp, effectiveCompanyId);
            successCount++;
          } catch (err) {
            console.error(`Error setting temp for room ${room.roomName}:`, err);
          }
        }
      }
      setMessage({ type: 'success', text: `${successCount} odanın sıcaklığı ${bulkSetTemp}°C olarak ayarlandı` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Toplu Çalışma Modu
  const handleBulkOpMode = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let successCount = 0;
      for (const room of rooms) {
        if (room.opModeSetupTagId) {
          try {
            await tagService.updateTagValue(room.opModeSetupTagId, bulkOpMode, effectiveCompanyId);
            successCount++;
          } catch (err) {
            console.error(`Error setting mode for room ${room.roomName}:`, err);
          }
        }
      }
      const modeName = opModeOptions.find(o => o.value === bulkOpMode)?.label || bulkOpMode;
      setMessage({ type: 'success', text: `${successCount} odanın modu ${modeName} olarak ayarlandı` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Toplu Fan Modu
  const handleBulkFanMode = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let successCount = 0;
      for (const room of rooms) {
        if (room.fanSetupTagId) {
          try {
            await tagService.updateTagValue(room.fanSetupTagId, bulkFanMode, effectiveCompanyId);
            successCount++;
          } catch (err) {
            console.error(`Error setting fan mode for room ${room.roomName}:`, err);
          }
        }
      }
      const fanName = fanModeOptions.find(o => o.value === bulkFanMode)?.label || bulkFanMode;
      setMessage({ type: 'success', text: `${successCount} odanın fan modu ${fanName} olarak ayarlandı` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Ana Buton */}
      <Box
        onClick={handleOpenDialog}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: backgroundColor,
          color: foreColor,
          border: `${borderWidth}px solid ${borderColor}`,
          borderRadius: `${borderRadius}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: isEditMode ? 'default' : 'pointer',
          fontWeight: 'bold',
          fontSize: fontSize,
          '&:hover': {
            opacity: isEditMode ? 1 : 0.9,
            boxShadow: isEditMode ? 'none' : '0 2px 8px rgba(0,0,0,0.2)'
          }
        }}
      >
        <SettingsIcon sx={{ fontSize: fontSize + 4 }} />
        <span>{elementDetails?.text || properties?.text || text}</span>
      </Box>

      {/* Kontrol Popup */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 400
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#edbc45',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {effectiveFloorName} - Toplu VRF Kontrol
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {/* Bilgi */}
          <Alert severity="info" sx={{ mb: 3 }}>
            Bu panelden <strong>{rooms.length} oda</strong> için toplu kontrol yapabilirsiniz.
          </Alert>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {/* AÇ/KAPAT Bölümü */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PowerSettingsNewIcon color="primary" />
              Cihaz Kontrolü
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleTurnAllOn}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PowerSettingsNewIcon />}
                sx={{ flex: 1, py: 1.5 }}
              >
                TÜMÜNÜ AÇ
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleTurnAllOff}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PowerSettingsNewIcon />}
                sx={{ flex: 1, py: 1.5 }}
              >
                TÜMÜNÜ KAPAT
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Set Sıcaklık Bölümü */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThermostatIcon color="primary" />
              Toplu Sıcaklık Ayarı
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                type="number"
                value={bulkSetTemp}
                onChange={(e) => setBulkSetTemp(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: <Typography>°C</Typography>,
                  inputProps: { min: 16, max: 30 }
                }}
                sx={{ width: 150 }}
              />
              <Button
                variant="contained"
                onClick={handleBulkSetTemp}
                disabled={loading}
                sx={{ width: 200, py: 1, backgroundColor: '#edbc45', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#d4a73d' } }}
              >
                TÜMÜNE UYGULA
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Çalışma Modu Bölümü */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AirIcon color="primary" />
              Toplu Çalışma Modu
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 150 }}>
                <Select
                  value={bulkOpMode}
                  onChange={(e) => setBulkOpMode(e.target.value)}
                >
                  {opModeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleBulkOpMode}
                disabled={loading}
                sx={{ width: 200, py: 1, backgroundColor: '#edbc45', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#d4a73d' } }}
              >
                TÜMÜNE UYGULA
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Fan Modu Bölümü */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AirIcon color="primary" />
              Toplu Fan Modu
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 150 }}>
                <Select
                  value={bulkFanMode}
                  onChange={(e) => setBulkFanMode(e.target.value)}
                >
                  {fanModeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleBulkFanMode}
                disabled={loading}
                sx={{ width: 200, py: 1, backgroundColor: '#edbc45', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#d4a73d' } }}
              >
                TÜMÜNE UYGULA
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VRFFloorControl;
